import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import {
  PRACTICE_LABELS,
  attemptTimestamp,
  buildPracticeAttemptPath,
  buildPracticeHistoryPath,
  fetchPracticeAttemptHistory,
  normalizeAttempt,
  practiceScoreLabel,
} from '../lib/practiceAttempts';

const studentId = (student) => String(student?._id || student?.id || student?.student_id || '');

const studentLabel = (student) => (
  student?.nickname
  || student?.display_name
  || student?.username
  || student?.name
  || studentId(student)
);

const targetStudentId = (target) => String(
  target?.student_id
  || target?.student?.id
  || target?.student?._id
  || '',
);

const targetCompleted = (target) => (
  target?.effective_status === 'completed'
  || target?.status === 'completed'
  || Boolean(target?.completed_at)
);

const formatDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale === 'zh-CN' ? 'zh-CN' : 'en', {
    timeZone: 'Asia/Shanghai',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export default function AdminPracticeAssignmentDetailModal({ assignment, classStudents = [], onClose }) {
  const { locale, t } = useI18n();
  const [report, setReport] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const history = await fetchPracticeAttemptHistory({
          assignmentId: assignment.id,
          viewer: 'admin',
          page: 1,
          pageSize: 500,
        });
        if (!active) return;
        setReport(history.report || null);
        setAttempts(history.attempts || []);
      } catch (loadError) {
        if (active) setError(loadError.message || t('The practice history could not be loaded.'));
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [assignment.id, t]);

  const studentsById = useMemo(() => new Map(
    classStudents.map((student) => [studentId(student), student]),
  ), [classStudents]);

  const targets = useMemo(() => (
    Array.isArray(report?.targets) ? report.targets : []
  ), [report?.targets]);

  const completedTargets = targets.filter(targetCompleted);
  const pendingTargets = targets.filter((target) => !targetCompleted(target));
  const practiceType = assignment.practice_type || report?.assignment?.practice_type || '';
  const completionRate = report?.completion_rate == null
    ? (targets.length ? Math.round((completedTargets.length / targets.length) * 100) : 0)
    : Math.round(Number(report.completion_rate) * (Number(report.completion_rate) <= 1 ? 100 : 1));

  const labelForTarget = (target) => {
    const id = targetStudentId(target);
    return studentLabel(studentsById.get(id)) || id || t('Student');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3" role="dialog" aria-modal="true" aria-labelledby="practice-assignment-detail-title">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{t(PRACTICE_LABELS[practiceType] || 'Practice')}</div>
            <h2 id="practice-assignment-detail-title" className="mt-1 truncate text-xl font-bold text-gray-950" data-i18n-skip>{assignment.title || t('Practice')}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded border border-gray-300 p-2 text-gray-600 hover:bg-gray-50" title={t('Close')} aria-label={t('Close')}>
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="overflow-y-auto px-5 py-5">
          {loading && <p className="py-10 text-center text-gray-600">{t('Loading attempt history...')}</p>}
          {error && <div className="border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">{t(error)}</div>}
          {!loading && !error && (
            <>
              <div className="flex flex-wrap gap-3">
                <span className="border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-900">
                  {t('Completion: {rate}% ({completed}/{total})', {
                    rate: completionRate,
                    completed: completedTargets.length,
                    total: targets.length,
                  })}
                </span>
                <span className="border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-semibold text-gray-700">
                  {t('{count} attempts', { count: attempts.length })}
                </span>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <section className="border border-gray-200">
                  <h3 className="border-b border-gray-200 bg-gray-50 px-3 py-2 font-bold text-gray-900">{t('Completed students ({count})', { count: completedTargets.length })}</h3>
                  <div className="max-h-72 overflow-y-auto divide-y divide-gray-200">
                    {completedTargets.length === 0 && <p className="p-4 text-sm text-gray-500">{t('No students have completed this practice yet.')}</p>}
                    {completedTargets.map((target) => {
                      const firstAttempt = target.first_attempt ? normalizeAttempt(target.first_attempt) : null;
                      const score = firstAttempt ? practiceScoreLabel(firstAttempt, practiceType) : '';
                      return (
                        <div key={target.id || targetStudentId(target)} className="flex items-center justify-between gap-3 px-3 py-3 text-sm">
                          <span className="font-semibold text-gray-900" data-i18n-skip>{labelForTarget(target)}</span>
                          <span className="text-gray-600">{score ? `${t('First attempt')}: ${score}` : t('Completed')}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="border border-gray-200">
                  <h3 className="border-b border-gray-200 bg-gray-50 px-3 py-2 font-bold text-gray-900">{t('Pending students ({count})', { count: pendingTargets.length })}</h3>
                  <div className="max-h-72 overflow-y-auto divide-y divide-gray-200">
                    {pendingTargets.length === 0 && <p className="p-4 text-sm text-gray-500">{t('No pending students.')}</p>}
                    {pendingTargets.map((target) => (
                      <div key={target.id || targetStudentId(target)} className="px-3 py-3 text-sm font-semibold text-gray-800" data-i18n-skip>{labelForTarget(target)}</div>
                    ))}
                  </div>
                </section>
              </div>

              <section className="mt-5 border-t border-gray-200 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-gray-900">{t('All attempts')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Open an attempt to review its saved result and details.')}</p>
                  </div>
                  <Link to={buildPracticeHistoryPath(assignment.id, 'admin')} className="text-sm font-semibold text-indigo-700 hover:underline">{t('Open full report')}</Link>
                </div>
                <div className="mt-3 divide-y divide-gray-200 border-y border-gray-200">
                  {attempts.length === 0 && <p className="p-5 text-center text-sm text-gray-500">{t('No attempts found for this practice.')}</p>}
                  {attempts.map((attempt) => {
                    const normalized = normalizeAttempt(attempt);
                    const student = studentsById.get(String(attempt.student_id || attempt.user_id || ''));
                    const score = practiceScoreLabel(normalized, practiceType);
                    return (
                      <div key={normalized.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0 text-sm">
                          <span className="font-semibold text-gray-900" data-i18n-skip>{studentLabel(student) || attempt.student_name || attempt.username || t('Student')}</span>
                          <span className="ml-3 text-gray-500" data-i18n-skip>{formatDate(attemptTimestamp(normalized), locale)}</span>
                          {score && <span className="ml-3 font-semibold text-indigo-700" data-i18n-skip>{score}</span>}
                        </div>
                        <Link to={buildPracticeAttemptPath(normalized.id, 'admin')} className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-700 hover:underline">
                          {t('View details')}
                          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
